/**
 * BasemapToolCard — Card 1 from
 * `VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md` §7.1.
 *
 * Implements the full basemap surface (switch + on/off + opacity + style +
 * labels + brightness/contrast + coordinate readout + scale bar slot +
 * north arrow + reset/home + geocoder + screenshot/share). The card is
 * rendered by the right-rail `ToolCardDock` whenever
 * `useViewerStore.sceneSelection.toolCardKind === 'basemap'`.
 *
 * The card emits its mutations through `useViewerStore.setBasemapToolCard`
 * so adapter mocks and tests can observe a stable contract. The MapLibre
 * adapter side-effect (swapping tile sources) is delegated to the
 * existing `applyBasemap` helper when a container is provided — keeping
 * the engine binding optional so the card stays unit-testable without a
 * mounted map.
 *
 * Design-system compliance (WP-3):
 *   - Tokens only; no raw hex / rgba / pixel literals in this file.
 *   - Material Symbols rounded only (`map`, `image`, `tune`, `flare`,
 *     `contrast`, `north`, `restart_alt`, `screenshot_monitor`, `block`,
 *     `search`).
 *   - 40 px minimum touch target on every chip, slider thumb, and button.
 *   - One primary CTA per action row (the geocoder search lives in the
 *     coordinate-readout section; screenshot/share is a single CTA in
 *     the header slot).
 *   - Numeric readouts (opacity %, brightness ×, contrast ×, coordinate)
 *     in mono via `tokens.font.role.engMono`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_BASEMAP_ID,
  applyBasemap,
  getAllBasemaps,
  getBasemap,
  savePreferredBasemapId,
  type BasemapId,
  type BasemapDefinition,
} from '@/engines/maplibre/basemaps';
import {
  addCustomBasemap,
  isCustomBasemapId,
  removeCustomBasemap,
  type CustomBasemapProtocol,
} from '@/engines/maplibre/customBasemaps';
import { getMapLibreHostHandle } from '@/engines/maplibre/hostHandle';
import { getCanvasMapHostHandle } from '@/engines/openlayers/hostHandle';
import {
  Banner,
  Chip,
  Icon,
  tokens,
  type ChipVariant,
} from '@/design';
import {
  ToolCardSection,
  ToolCardShell,
  ToolCardSliderRow,
  ToolCardToggleRow,
} from '@/components/toolcards/ToolCardShell';
import { BasemapPreview } from '@/components/basemap/BasemapPreview';
import { useViewerStore } from '@/state/useViewerStore';
import { useViewerSlice } from '@/state/useViewerSlice';

export type BasemapTileHealth =
  | 'rest'
  | 'loading'
  | 'partial'
  | 'offline'
  | 'stale'
  | 'low-accuracy';

export interface BasemapCoordinate {
  readonly lon: number;
  readonly lat: number;
}

export interface BasemapToolCardProps {
  /** Map container; required to swap basemaps on the live MapLibre host. */
  container?: HTMLElement | null;
  /** Current cursor coordinate (lng/lat); rendered in mono in §7.1 row. */
  coordinate?: BasemapCoordinate | null;
  /** Map scale denominator (e.g. 250000 → "1:250 000"). */
  scaleDenominator?: number | null;
  /** Map bearing in degrees (0 = north). Drives the compass widget. */
  bearingDeg?: number | null;
  /** Tile health for the loading/stale/offline banner row. */
  tileHealth?: BasemapTileHealth;
  /** Optional reset-view callback (fit project bbox / home view). */
  onResetView?: () => void;
  /** Optional reset-to-north callback (zero bearing). */
  onResetNorth?: () => void;
  /** Optional geocoder open callback. */
  onOpenGeocoder?: () => void;
  /** Optional screenshot/share view callback. */
  onShareView?: () => void;
  /** Apply the selected basemap to a non-MapLibre engine, such as Giro3D. */
  onBasemapChange?: (id: BasemapId) => void;
  /** Read-only / permission-denied banner content (string). */
  permissionDenied?: string | null;
  /** Override the "only render when basemap selected" gate (for testing). */
  forceVisible?: boolean;
  'data-testid'?: string;
}

const TILE_HEALTH_TO_CHIP: Record<
  BasemapTileHealth,
  { label: string; variant: ChipVariant; icon: 'sync_problem' | 'cloud_off' | 'wrong_location' | 'check_circle' | 'pending' }
> = {
  rest: { label: 'Tiles ready', variant: 'success', icon: 'check_circle' },
  loading: { label: 'Loading tiles', variant: 'info', icon: 'pending' },
  partial: { label: 'Partial tiles', variant: 'warning', icon: 'sync_problem' },
  offline: { label: 'Offline tiles', variant: 'warning', icon: 'cloud_off' },
  stale: { label: 'Stale tiles', variant: 'warning', icon: 'sync_problem' },
  'low-accuracy': {
    label: 'Low-accuracy basemap',
    variant: 'warning',
    icon: 'wrong_location',
  },
};

function formatOpacity(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatMultiplier(value: number): string {
  return `${value.toFixed(2)}×`;
}

function formatScale(denom: number | null | undefined): string {
  if (denom == null || !Number.isFinite(denom)) return '—';
  const rounded = Math.round(denom);
  return `1:${rounded.toLocaleString('en-US')}`;
}

function formatBearing(deg: number | null | undefined): string {
  if (deg == null || !Number.isFinite(deg)) return '—';
  const normalized = ((deg % 360) + 360) % 360;
  return `${normalized.toFixed(1)}°`;
}

function formatLngLat(p: BasemapCoordinate | null | undefined): string {
  if (!p) return '— , —';
  return `${p.lon.toFixed(6)}, ${p.lat.toFixed(6)}`;
}

export function BasemapToolCard({
  container,
  coordinate,
  scaleDenominator,
  bearingDeg,
  tileHealth = 'rest',
  onResetView,
  onResetNorth,
  onOpenGeocoder,
  onShareView,
  onBasemapChange,
  permissionDenied,
  forceVisible,
  'data-testid': testId,
}: BasemapToolCardProps) {
  const toolCardKind = useViewerSlice(
    (s) => s.sceneSelection.toolCardKind ?? 'gtwin',
  );
  const state = useViewerSlice((s) => s.basemapToolCard);
  const setBasemapToolCard = useViewerStore((s) => s.setBasemapToolCard);
  const [galleryRevision, setGalleryRevision] = useState(0);
  const [customFormOpen, setCustomFormOpen] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  const [customProtocol, setCustomProtocol] =
    useState<CustomBasemapProtocol>('xyz');
  const [customUrl, setCustomUrl] = useState('');
  const [customAttribution, setCustomAttribution] = useState('');
  const [customError, setCustomError] = useState<string | null>(null);

  const gallery = useMemo(() => getAllBasemaps(), [galleryRevision]);

  const active: BasemapDefinition = useMemo(
    () => getBasemap(state.activeBasemapId),
    [state.activeBasemapId, galleryRevision],
  );
  // Raster basemaps support brightness / contrast; the "none" entry does
  // not. Detect via the presence of any layer in the registry definition.
  const supportsRasterTone = active.layers.length > 0;
  const activeOptionRef = useRef<HTMLButtonElement>(null);
  const visible = forceVisible || toolCardKind === 'basemap';

  // Keep the selected basemap card in view when the gallery opens.
  useEffect(() => {
    if (!visible) return;
    const frame = window.requestAnimationFrame(() => {
      activeOptionRef.current?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [visible, state.activeBasemapId, galleryRevision]);

  const applyToMap = useCallback(
    (id: BasemapId) => {
      if (!container) return;
      const map = getMapLibreHostHandle(container);
      if (map) {
        try {
          applyBasemap(map, id);
        } catch (err) {
          console.warn('[BasemapToolCard] applyBasemap failed', err);
        }
        return;
      }
      const canvas = getCanvasMapHostHandle(container);
      if (canvas) {
        try {
          canvas.setBasemap(id);
        } catch (err) {
          console.warn('[BasemapToolCard] canvas setBasemap failed', err);
        }
      }
    },
    [container],
  );

  const handleSelectBasemap = useCallback(
    (id: BasemapId) => {
      setBasemapToolCard({ activeBasemapId: id });
      savePreferredBasemapId(id);
      applyToMap(id);
      onBasemapChange?.(id);
    },
    [applyToMap, onBasemapChange, setBasemapToolCard],
  );

  const handleAddCustomBasemap = useCallback(() => {
    const result = addCustomBasemap({
      label: customLabel,
      protocol: customProtocol,
      url: customUrl,
      attribution: customAttribution,
    });
    if (!result.ok) {
      setCustomError(result.error);
      return;
    }
    setCustomError(null);
    setCustomLabel('');
    setCustomUrl('');
    setCustomAttribution('');
    setCustomFormOpen(false);
    setGalleryRevision((n) => n + 1);
    handleSelectBasemap(result.basemapId);
  }, [
    customAttribution,
    customLabel,
    customProtocol,
    customUrl,
    handleSelectBasemap,
  ]);

  const handleRemoveCustomBasemap = useCallback(
    (id: BasemapId) => {
      if (!isCustomBasemapId(id)) return;
      removeCustomBasemap(id);
      setGalleryRevision((n) => n + 1);
      if (state.activeBasemapId === id) {
        handleSelectBasemap(DEFAULT_BASEMAP_ID);
      }
    },
    [handleSelectBasemap, state.activeBasemapId],
  );

  if (!visible) return null;

  const tileChip = TILE_HEALTH_TO_CHIP[tileHealth];

  return (
    <ToolCardShell
      icon="map"
      title="Basemap & imagery"
      subtitle={active.label}
      data-testid={testId ?? 'basemap-toolcard'}
      primaryAction={
        onShareView ? (
          <button
            type="button"
            aria-label="Share or screenshot map view"
            onClick={onShareView}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              background: tokens.color.accent.primary,
              color: tokens.color.accent.on,
              border: `${tokens.border.width.thin}px solid transparent`,
              borderRadius: tokens.radius.input,
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
              fontWeight: tokens.font.weight.medium,
              cursor: 'pointer',
            }}
          >
            <Icon name="screenshot_monitor" size={20} decorative />
            Share
          </button>
        ) : null
      }
      topBanners={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.space[2],
          }}
        >
          {permissionDenied ? (
            <Banner
              severity="warning"
              title="Basemap restricted"
              body={permissionDenied}
              narrationKey="basemap-toolcard-permission"
            />
          ) : null}
          {tileHealth !== 'rest' ? (
            <Chip
              icon={tileChip.icon}
              variant={tileChip.variant}
              label={tileChip.label}
            />
          ) : null}
        </div>
      }
    >
      <ToolCardSection title="Imagery">
        <div
          role="radiogroup"
          aria-label="Basemap"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: tokens.space[2],
            maxHeight: 440,
            overflowY: 'auto',
            padding: tokens.space[1],
          }}
        >
          {gallery.map((b) => {
            const isActive = b.id === state.activeBasemapId;
            const isCustom = isCustomBasemapId(b.id);
            return (
              <div
                key={b.id}
                style={{ position: 'relative', display: 'flex' }}
              >
                <button
                  ref={isActive ? activeOptionRef : undefined}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => handleSelectBasemap(b.id)}
                  data-testid={`basemap-option-${b.id}`}
                  aria-label={`${b.label}: ${b.description}`}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    padding: 0,
                    minHeight: tokens.density.touchMin,
                    width: '100%',
                    background: isActive
                      ? tokens.color.accent.subtle
                      : tokens.color.surface[1],
                    color: tokens.color.ink.primary,
                    border: isActive
                      ? `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`
                      : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                    borderRadius: tokens.radius.input,
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontFamily: tokens.font.family.ui,
                    fontSize: tokens.font.size.sm,
                  }}
                >
                  <BasemapPreview basemap={b} />
                  <span
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: `${tokens.space[2]}px ${tokens.space[1]}px`,
                      fontWeight: tokens.font.weight.medium,
                    }}
                  >
                    {b.label}
                  </span>
                  {isActive ? (
                    <span
                      style={{
                        position: 'absolute',
                        top: tokens.space[1],
                        right: tokens.space[1],
                        display: 'grid',
                        placeItems: 'center',
                        width: tokens.density.touchMin,
                        height: tokens.density.touchMin,
                        borderRadius: tokens.radius.pill,
                        background: tokens.color.accent.primary,
                        color: tokens.color.accent.on,
                      }}
                    >
                      <Icon name="check" size={20} decorative />
                    </span>
                  ) : null}
                </button>
                {isCustom ? (
                  <button
                    type="button"
                    aria-label={`Remove ${b.label}`}
                    data-testid={`basemap-remove-${b.id}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleRemoveCustomBasemap(b.id);
                    }}
                    style={{
                      position: 'absolute',
                      top: tokens.space[1],
                      left: tokens.space[1],
                      zIndex: 1,
                      display: 'grid',
                      placeItems: 'center',
                      width: tokens.density.touchMin,
                      height: tokens.density.touchMin,
                      borderRadius: tokens.radius.pill,
                      border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                      background: tokens.color.surface[1],
                      color: tokens.color.ink.primary,
                      cursor: 'pointer',
                    }}
                  >
                    <Icon name="delete" size={20} decorative />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      </ToolCardSection>

      <ToolCardSection
        title="Custom XYZ / WMTS"
        description="Add a tile service the same way as in QGIS (Layer → Add Layer → Add XYZ/WMTS)."
      >
        {!customFormOpen ? (
          <button
            type="button"
            data-testid="basemap-add-custom"
            onClick={() => {
              setCustomError(null);
              setCustomFormOpen(true);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.input,
              cursor: 'pointer',
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
            }}
          >
            <Icon name="add" size={20} decorative />
            Add custom basemap
          </button>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: tokens.space[2],
            }}
          >
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.space[1],
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
              }}
            >
              Name
              <input
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                data-testid="basemap-custom-name"
                placeholder="My XYZ basemap"
                style={{
                  minHeight: tokens.density.touchMin,
                  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                  border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                  borderRadius: tokens.radius.input,
                  background: tokens.color.surface[1],
                  color: tokens.color.ink.primary,
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.role.body.size,
                }}
              />
            </label>
            <fieldset
              style={{
                margin: 0,
                padding: 0,
                border: 'none',
                display: 'flex',
                gap: tokens.space[2],
              }}
            >
              <legend
                style={{
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.role.label.size,
                  color: tokens.color.ink.secondary,
                  marginBottom: tokens.space[1],
                }}
              >
                Type
              </legend>
              {(['xyz', 'wmts'] as const).map((protocol) => (
                <button
                  key={protocol}
                  type="button"
                  aria-pressed={customProtocol === protocol}
                  data-testid={`basemap-custom-type-${protocol}`}
                  onClick={() => setCustomProtocol(protocol)}
                  style={{
                    minHeight: tokens.density.touchMin,
                    padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                    borderRadius: tokens.radius.input,
                    border:
                      customProtocol === protocol
                        ? `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`
                        : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                    background:
                      customProtocol === protocol
                        ? tokens.color.accent.subtle
                        : tokens.color.surface[1],
                    color: tokens.color.ink.primary,
                    cursor: 'pointer',
                    fontFamily: tokens.font.family.ui,
                    fontSize: tokens.font.role.body.size,
                    textTransform: 'uppercase',
                  }}
                >
                  {protocol}
                </button>
              ))}
            </fieldset>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.space[1],
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
              }}
            >
              URL template
              <input
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                data-testid="basemap-custom-url"
                placeholder={
                  customProtocol === 'wmts'
                    ? 'https://…/{TileMatrix}/{TileRow}/{TileCol}.png'
                    : 'https://…/{z}/{x}/{y}.png'
                }
                style={{
                  minHeight: tokens.density.touchMin,
                  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                  border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                  borderRadius: tokens.radius.input,
                  background: tokens.color.surface[1],
                  color: tokens.color.ink.primary,
                  fontFamily: tokens.font.family.mono,
                  fontSize: tokens.font.role.engMono.size,
                }}
              />
            </label>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.space[1],
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
              }}
            >
              Attribution (optional)
              <input
                value={customAttribution}
                onChange={(e) => setCustomAttribution(e.target.value)}
                data-testid="basemap-custom-attribution"
                placeholder="© Data provider"
                style={{
                  minHeight: tokens.density.touchMin,
                  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                  border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                  borderRadius: tokens.radius.input,
                  background: tokens.color.surface[1],
                  color: tokens.color.ink.primary,
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.role.body.size,
                }}
              />
            </label>
            {customError ? (
              <Banner
                severity="warning"
                title="Could not add basemap"
                body={customError}
                narrationKey="basemap-custom-error"
              />
            ) : null}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: tokens.space[2],
              }}
            >
              <button
                type="button"
                data-testid="basemap-custom-save"
                onClick={handleAddCustomBasemap}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: tokens.space[1],
                  minHeight: tokens.density.touchMin,
                  padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                  background: tokens.color.accent.primary,
                  color: tokens.color.accent.on,
                  border: `${tokens.border.width.thin}px solid transparent`,
                  borderRadius: tokens.radius.input,
                  cursor: 'pointer',
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.role.body.size,
                  fontWeight: tokens.font.weight.medium,
                }}
              >
                <Icon name="save" size={20} decorative />
                Save & apply
              </button>
              <button
                type="button"
                data-testid="basemap-custom-cancel"
                onClick={() => {
                  setCustomFormOpen(false);
                  setCustomError(null);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: tokens.space[1],
                  minHeight: tokens.density.touchMin,
                  padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                  background: tokens.color.surface[1],
                  color: tokens.color.ink.primary,
                  border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                  borderRadius: tokens.radius.input,
                  cursor: 'pointer',
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.role.body.size,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </ToolCardSection>

      <ToolCardSection title="Visibility">
        <ToolCardToggleRow
          label="Basemap visible"
          description="Show the basemap behind your data layers."
          icon="visibility"
          checked={state.visible}
          onChange={(next) => setBasemapToolCard({ visible: next })}
          data-testid="basemap-visible-toggle"
        />
        <ToolCardSliderRow
          label="Opacity"
          icon="tune"
          min={0}
          max={1}
          step={0.05}
          value={state.opacity}
          readout={formatOpacity(state.opacity)}
          onChange={(next) => setBasemapToolCard({ opacity: next })}
          data-testid="basemap-opacity-slider"
        />
        <ToolCardToggleRow
          label="Labels"
          icon="label"
          checked={state.labelsOn}
          onChange={(next) => setBasemapToolCard({ labelsOn: next })}
          data-testid="basemap-labels-toggle"
        />
      </ToolCardSection>

      {supportsRasterTone ? (
        <ToolCardSection
          title="Raster tone"
          description="Brightness / contrast only apply to raster basemaps."
        >
          <ToolCardSliderRow
            label="Brightness"
            icon="flare"
            min={0.5}
            max={1.5}
            step={0.05}
            value={state.brightness}
            readout={formatMultiplier(state.brightness)}
            onChange={(next) => setBasemapToolCard({ brightness: next })}
            data-testid="basemap-brightness-slider"
          />
          <ToolCardSliderRow
            label="Contrast"
            icon="contrast"
            min={0.5}
            max={1.5}
            step={0.05}
            value={state.contrast}
            readout={formatMultiplier(state.contrast)}
            onChange={(next) => setBasemapToolCard({ contrast: next })}
            data-testid="basemap-contrast-slider"
          />
        </ToolCardSection>
      ) : (
        <ToolCardSection title="Raster tone">
          <Chip
            icon="block"
            variant="neutral"
            label="No raster basemap selected"
          />
        </ToolCardSection>
      )}

      <ToolCardSection title="Coordinate & scale">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: tokens.space[2],
            fontFamily: tokens.font.family.mono,
            fontSize: tokens.font.role.engMono.size,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Lng / Lat
            </div>
            <div>{formatLngLat(coordinate)}</div>
          </div>
          <div>
            <div
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Scale
            </div>
            <div>{formatScale(scaleDenominator)}</div>
          </div>
          <div>
            <div
              style={{
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.role.label.size,
                color: tokens.color.ink.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Bearing
            </div>
            <div>{formatBearing(bearingDeg)}</div>
          </div>
        </div>
      </ToolCardSection>

      <ToolCardSection title="Navigation">
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: tokens.space[2],
          }}
        >
          <button
            type="button"
            onClick={onResetView}
            disabled={!onResetView}
            data-testid="basemap-reset-view"
            aria-label="Reset to project home view"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.input,
              cursor: onResetView ? 'pointer' : 'default',
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
              opacity: onResetView ? 1 : 0.5,
            }}
          >
            <Icon name="restart_alt" size={20} decorative />
            Home view
          </button>
          <button
            type="button"
            onClick={onResetNorth}
            disabled={!onResetNorth}
            data-testid="basemap-reset-north"
            aria-label="Reset bearing to north"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.input,
              cursor: onResetNorth ? 'pointer' : 'default',
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
              opacity: onResetNorth ? 1 : 0.5,
            }}
          >
            <Icon name="north" size={20} decorative />
            Reset north
          </button>
          <button
            type="button"
            onClick={onOpenGeocoder}
            disabled={!onOpenGeocoder}
            data-testid="basemap-open-geocoder"
            aria-label="Open geocoder search"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[1],
              minHeight: tokens.density.touchMin,
              padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
              background: tokens.color.surface[1],
              color: tokens.color.ink.primary,
              border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
              borderRadius: tokens.radius.input,
              cursor: onOpenGeocoder ? 'pointer' : 'default',
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
              opacity: onOpenGeocoder ? 1 : 0.5,
            }}
          >
            <Icon name="search" size={20} decorative />
            Geocoder
          </button>
        </div>
      </ToolCardSection>
    </ToolCardShell>
  );
}
